import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { useNotify, useDataProvider } from 'react-admin';
import { LEAD_VALIDATION_RULES, createFieldValidator, createFormValidator } from '../utils/leadValidators';
import logger from '../../utils/logger';

interface FormData {
  name: string;
  email: string;
  phoneNumber: string;
  address: string;
  city: string;
  state: string;
  zipCode: string;
  language: string;
  birthDate: string;
  sourceOrgId: string;
  status: string;
  isArchived: boolean;
  smsOptIn: boolean;
  emailOptIn: boolean; // Derived from emailSubscriptionGroupId existence
  smsSubscriptionGroupId: string;
  emailSubscriptionGroupId: string;
  wicClinicCode: string;
}

const initialFormData: FormData = {
  name: '',
  email: '',
  phoneNumber: '',
  address: '',
  city: '',
  state: '',
  zipCode: '',
  language: '',
  birthDate: '',
  sourceOrgId: '',
  status: 'active',
  isArchived: false,
  smsOptIn: true,
  emailOptIn: false,
  smsSubscriptionGroupId: '',
  emailSubscriptionGroupId: '',
  wicClinicCode: '',
};

interface UseEditLeadModalReturn {
  formData: FormData;
  setFormData: React.Dispatch<React.SetStateAction<FormData>>;
  errors: Record<string, string | undefined>;
  isSubmitting: boolean;
  isLoading: boolean;
  validateField: (field: string, value: any) => string | undefined;
  validateForm: () => Record<string, string | undefined>;
  resetForm: () => void;
  handleSubmit: () => Promise<void>;
  forceLoadData: () => void;
}

export const useEditLeadModal = (leadId: number | string | undefined, onSuccess?: (data: any) => void, onClose?: () => void): UseEditLeadModalReturn => {
  const [formData, setFormData] = useState<FormData>(initialFormData);
  const [originalFormData, setOriginalFormData] = useState<FormData>(initialFormData);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);
  const notify = useNotify();
  const dataProvider = useDataProvider();

  const loadLeadData = useCallback(async () => {
    if (!leadId) {
      logger.log('useEditLeadModal: No leadId provided, skipping load');
      return;
    }

    try {
      logger.log('useEditLeadModal: Starting to load lead data for ID:', leadId);
      setIsLoading(true);
      const response = await dataProvider.getOne('leads', { id: leadId });
      // Avoid logging full API responses (may contain PII).
      logger.log('useEditLeadModal: Lead data received for ID:', leadId);

      // Map API response to form data
      const leadData = response.data;

      // Use only emailSubscriptionGroupId and smsSubscriptionGroupId (subscriptionGroupId is deprecated, do not use)
      const emailSubscriptionGroupId = leadData.emailSubscriptionGroupId || '';
      const smsSubscriptionGroupId = leadData.smsSubscriptionGroupId || '';

      // Use explicit emailOptIn from backend (returns 0 or 1, or None for legacy data)
      // Backend already handles fallback to subscription group ID for legacy leads
      // Only fallback to subscription group ID if explicit value is truly missing (undefined/null)
      const emailOptIn = leadData.emailOptIn !== undefined && leadData.emailOptIn !== null
        ? Boolean(leadData.emailOptIn)
        : (leadData.email_opt_in !== undefined && leadData.email_opt_in !== null
          ? Boolean(leadData.email_opt_in)
          : !!emailSubscriptionGroupId); // Fallback for very old legacy leads without explicit opt-in

      const loadedFormData = {
        name: leadData.name || '',
        email: leadData.email || '',
        phoneNumber: leadData.phoneNumber || '',
        address: leadData.address || '',
        city: leadData.city || '',
        state: leadData.state || '',
        zipCode: leadData.zipCode || leadData.zip_code || '',
        language: leadData.language || '',
        birthDate: leadData.birthDate || '',
        sourceOrgId: leadData.sourceOrgId || leadData.source_org_id || '',
        status: leadData.status || 'active',
        isArchived:
          leadData.isArchived !== undefined && leadData.isArchived !== null
            ? Boolean(leadData.isArchived)
            : (leadData.is_archived !== undefined && leadData.is_archived !== null ? Boolean(leadData.is_archived) : false),
        smsOptIn: leadData.smsOptIn || leadData.sms_opt_in || false,
        emailOptIn: emailOptIn,
        smsSubscriptionGroupId: smsSubscriptionGroupId,
        emailSubscriptionGroupId: emailSubscriptionGroupId,
        wicClinicCode: leadData.wicClinicCode || leadData.wic_clinic_code || '',
      };
      setFormData(loadedFormData);
      setOriginalFormData(loadedFormData);
      setHasLoaded(true);
      logger.log('useEditLeadModal: Lead data loaded and form updated');
    } catch (error) {
      // Avoid logging full error objects (may include request/response details).
      // eslint-disable-next-line no-console
      logger.error('Error loading lead data:', (error as any)?.message || 'unknown_error');
      notify('Failed to load lead data', { type: 'error' });
    } finally {
      setIsLoading(false);
    }
  }, [leadId, dataProvider, notify]);

  // Load lead data when leadId changes
  useEffect(() => {
    logger.log('useEditLeadModal: leadId changed to:', leadId);
    if (leadId && !hasLoaded) {
      logger.log('useEditLeadModal: Loading lead data for ID:', leadId);
      loadLeadData();
    }
  }, [leadId, hasLoaded, loadLeadData]);

  const validateField = useCallback((field, value) => {
    const error = createFieldValidator(LEAD_VALIDATION_RULES)(field, value);
    setErrors(prev => ({ ...prev, [field]: error }));
    return error;
  }, []);

  const validateForm = useCallback(() => {
    const newErrors = createFormValidator(LEAD_VALIDATION_RULES)(formData);
    setErrors(newErrors);
    return newErrors;
  }, [formData]);

  const resetForm = useCallback(() => {
    setFormData(initialFormData);
    setOriginalFormData(initialFormData);
    setErrors({});
    setIsSubmitting(false);
    setHasLoaded(false);
    logger.log('useEditLeadModal: Form reset, will reload data on next open');
  }, []);

  // Check if form has changes (computed value)
  // Optimized: compare field-by-field instead of JSON.stringify for better performance
  const hasFormChanges = useMemo(() => {
    if (!hasLoaded) return false;

    // Quick field-by-field comparison (more efficient than JSON.stringify)
    const fieldsToCompare = ['name', 'email', 'phoneNumber', 'address', 'city', 'state', 'zipCode', 'language', 'birthDate', 'sourceOrgId', 'status', 'isArchived', 'smsOptIn', 'emailOptIn', 'smsSubscriptionGroupId', 'emailSubscriptionGroupId', 'wicClinicCode'];
    return fieldsToCompare.some(field => {
      const current = formData[field];
      const original = originalFormData[field];
      // Handle both string and boolean comparisons
      return String(current || '') !== String(original || '');
    });
  }, [formData, originalFormData, hasLoaded]);

  // Check if form is valid for submission (computed value, not function)
  // Use a version that doesn't update state to avoid re-render loops
  // Note: This is kept for potential future use but not used for button disabling
  const isFormValid = useMemo(() => {
    // Check required fields
    if (!formData.name?.trim()) return false;
    if (!formData.state?.trim()) return false;
    if (!formData.address?.trim()) return false;
    if (!formData.zipCode?.trim()) return false;
    if (!formData.sourceOrgId) return false;

    // Check that at least one contact method is provided
    const email = formData.email?.trim();
    const phoneNumber = formData.phoneNumber?.trim();
    if (!email && !phoneNumber) return false;

    // Check for validation errors (without updating state)
    const validationErrors = createFormValidator(LEAD_VALIDATION_RULES)(formData);
    return Object.keys(validationErrors).length === 0;
  }, [formData]);

  const handleSubmit = useCallback(async () => {
    if (!leadId) return;

    try {
      setIsSubmitting(true);

      // Prepare data for API
      // Use explicit boolean opt-in fields from toggles to control subscription state
      // Subscription group IDs are metadata - they can be empty/not set, but opt-in booleans control the action
      const apiData: any = {
        name: formData.name.trim(),
        email: formData.email?.trim() ? formData.email.trim().toLowerCase() : null,
        phoneNumber: formData.phoneNumber?.trim() || null,
        address: formData.address?.trim() || null,
        city: formData.city?.trim() || null,
        state: formData.state?.trim() || null,
        zip_code: formData.zipCode?.trim() || null,
        language: formData.language?.trim() || null,
        birthDate: formData.birthDate || null,
        sourceOrgId: formData.sourceOrgId || null,
        status: formData.status,
        isArchived: formData.isArchived, // Archive state toggle
        smsOptIn: formData.smsOptIn,  // Explicit boolean from toggle
        emailOptIn: formData.emailOptIn,  // Explicit boolean from toggle (new field)
        wicClinicCode: formData.wicClinicCode?.trim() || null,
      };

      // Note: We don't send emailSubscriptionGroupId or smsSubscriptionGroupId here
      // Backend will set/clear them based on emailOptIn and smsOptIn boolean values
      // If opt-in is enabled, backend will sync to Braze and set subscription group IDs
      // If opt-in is disabled, backend will unsubscribe from Braze and clear subscription group IDs

      // Remove null values
      Object.keys(apiData).forEach(key => {
        if (apiData[key] === null || apiData[key] === '') {
          delete apiData[key];
        }
      });

      // Use data provider to update lead
      const response = await dataProvider.update('leads', {
        id: leadId,
        data: apiData,
        previousData: originalFormData // Required by react-admin UpdateParams
      });

      notify('Lead updated successfully', { type: 'success' });

      // Call onSuccess before closing to ensure list refresh
      onSuccess?.(response.data);

      // Update originalFormData to reflect the saved changes
      setOriginalFormData(formData);

      // Close modal (don't reset form to keep data visible if reopened)
      onClose?.();

    } catch (error) {
      // Avoid logging full error objects (may include request/response details).
      // eslint-disable-next-line no-console
      logger.error('Error updating lead:', (error as any)?.message || 'unknown_error');
      notify((error as any)?.message || 'Failed to update lead', { type: 'error' });
    } finally {
      setIsSubmitting(false);
    }
  }, [leadId, formData, dataProvider, notify, onSuccess, onClose]);

  const forceLoadData = useCallback(() => {
    logger.log('useEditLeadModal: Force loading data for leadId:', leadId);
    setHasLoaded(false);
    if (leadId) {
      loadLeadData();
    }
  }, [leadId, loadLeadData]);

  return {
    formData,
    setFormData,
    errors,
    isSubmitting,
    isLoading,
    validateField,
    validateForm,
    resetForm,
    handleSubmit,
    forceLoadData,
  };
};
